import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import useCompanyAccess from '../hooks/company/useCompanyAccess';
import useAccessWaitGaveUp from '../hooks/company/useAccessWaitGaveUp';
import { openOfficeLockDialogWhenReady } from '../utils/company/officeLockDialogBus';
import { prefetchLogJobChunk } from '../utils/ui/prefetchLogJob';
import { roleHomePath } from '../utils/ui/roleHome';
import LogJobRouteFallback from './LogJobRouteFallback';

/**
 * KAN-827 — "stop at the door" for the /log-job/:customerId ROUTE (a deep link,
 * a saved last route, an AI action, a stale tab). A SUSPENDED company never
 * renders the form: the user lands on their role home (replace — no stale
 * back-stack entry) and the read-only dialog opens there. A read-only company
 * keeps logging visits (field work), and a paying one is untouched.
 *
 * While the access level is still loading the route shows the Log Job skeleton
 * and warms the Log Job chunk in parallel, so the normal deep link costs about
 * what it did before. If the read never answers (3s) the form renders — client
 * UX only, the server is the boundary and its refusal reaches the in-form notice.
 *
 * The lazy chunk's Suspense lives in App.js (LogJobRouteFallback), around this door.
 */
export default function LogJobRouteDoor({ children }) {
  const { role, tenantId } = useAuth();
  const { level, status } = useCompanyAccess();
  const suspended = level === 'suspended';
  const gaveUp = useAccessWaitGaveUp(status);

  useEffect(() => {
    if (status !== 'loading') return undefined;
    Promise.resolve(prefetchLogJobChunk()).catch(() => {});
    return undefined;
  }, [status]);

  useEffect(() => {
    if (suspended) openOfficeLockDialogWhenReady(tenantId);
  }, [suspended, tenantId]);

  if (suspended) return <Navigate to={roleHomePath(role)} replace />;
  if (status === 'loading' && !gaveUp) return <LogJobRouteFallback />;
  return children;
}

LogJobRouteDoor.propTypes = { children: PropTypes.node };
